import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  DollarSign, User, Briefcase, Calendar, CheckCircle2, XCircle, 
  Clock, AlertCircle, ThumbsUp, ThumbsDown, Plus, Loader2, ChevronRight, Pencil, Trash2
} from 'lucide-react';

const approvalStatusColors = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

const candidateDecisionColors = {
  pending: 'bg-gray-100 text-gray-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  negotiating: 'bg-yellow-100 text-yellow-800'
};

export default function OffersPage() {
  const { user, isAdmin, isSuperAdmin, isHR } = useAuth();
  const canManage = isAdmin || isSuperAdmin || isHR;
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    candidate_decision: ''
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState('approve');
  const [approvalComments, setApprovalComments] = useState('');
  const [candidateDecision, setCandidateDecision] = useState('accepted');
  const [joiningDate, setJoiningDate] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Create/Edit form state
  const defaultOfferForm = {
    candidate_id: '',
    position: '',
    department: '',
    base_salary: '',
    joining_bonus: '',
    variable_pay: '',
    stock_options: '',
    offer_expiry_date: '',
    proposed_joining_date: '',
    other_benefits: '',
    notes: ''
  };
  const [offerForm, setOfferForm] = useState(defaultOfferForm);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (filters.status) params.status = filters.status;
      if (filters.candidate_decision) params.candidate_decision = filters.candidate_decision;

      const response = await api.get('/recruitment/offers', { params });
      setOffers(response.data.offers || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await api.get('/recruitment/candidates', { params: { status: 'selected', limit: 100 } });
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await api.get('/recruitment/offers/pending-approvals');
      setPendingApprovals(response.data.offers || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  useEffect(() => {
    fetchOffers();
    fetchPendingApprovals();
    fetchCandidates();
  }, [page, filters]);

  const handleCreateOffer = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...offerForm,
        base_salary: parseFloat(offerForm.base_salary) || 0,
        joining_bonus: parseFloat(offerForm.joining_bonus) || 0,
        variable_pay: parseFloat(offerForm.variable_pay) || 0,
        stock_options: parseInt(offerForm.stock_options) || 0,
      };
      
      if (selectedOffer) {
        await api.put(`/recruitment/offers/${selectedOffer.id}`, payload);
        toast.success('Offer updated successfully');
      } else {
        // Use the simplified offer creation endpoint
        await api.post('/recruitment/offers/simple', payload);
        toast.success('Offer created successfully');
      }
      setShowCreateDialog(false);
      setSelectedOffer(null);
      setOfferForm(defaultOfferForm);
      fetchOffers();
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOffer = async () => {
    if (!selectedOffer) return;
    setSubmitting(true);
    try {
      await api.delete(`/recruitment/offers/${selectedOffer.id}`);
      toast.success('Offer deleted successfully');
      setShowDeleteDialog(false);
      setSelectedOffer(null);
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOffer = (offer) => {
    setSelectedOffer(offer);
    setOfferForm({
      candidate_id: offer.candidate_id || '',
      position: offer.position || '',
      department: offer.department || '',
      base_salary: offer.base_salary?.toString() || '',
      joining_bonus: offer.joining_bonus?.toString() || '',
      variable_pay: offer.variable_pay?.toString() || '',
      stock_options: offer.stock_options?.toString() || '',
      offer_expiry_date: offer.offer_expiry_date ? new Date(offer.offer_expiry_date).toISOString().split('T')[0] : '',
      proposed_joining_date: offer.proposed_joining_date ? new Date(offer.proposed_joining_date).toISOString().split('T')[0] : '',
      other_benefits: offer.other_benefits || '',
      notes: offer.notes || ''
    });
    setShowCreateDialog(true);
  };

  const openCreateDialog = () => {
    setSelectedOffer(null);
    setOfferForm(defaultOfferForm);
    setShowCreateDialog(true);
  };

  const handleApproval = async () => {
    if (!selectedOffer) return;
    setSubmitting(true);
    try {
      await api.post(`/recruitment/offers/${selectedOffer.id}/approve`, {
        action: approvalAction,
        comments: approvalComments
      });
      setShowApprovalDialog(false);
      setSelectedOffer(null);
      setApprovalComments('');
      fetchOffers();
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCandidateDecision = async () => {
    if (!selectedOffer) return;
    setSubmitting(true);
    try {
      await api.post(`/recruitment/offers/${selectedOffer.id}/candidate-decision`, {
        decision: candidateDecision,
        decline_reason: candidateDecision === 'declined' ? declineReason : null,
        actual_joining_date: candidateDecision === 'accepted' ? joiningDate : null
      });
      setShowDecisionDialog(false);
      setSelectedOffer(null);
      setCandidateDecision('accepted');
      setJoiningDate('');
      setDeclineReason('');
      fetchOffers();
    } catch (error) {
      console.error('Error recording decision:', error);
      alert(error.response?.data?.detail || 'Failed to record decision');
    } finally {
      setSubmitting(false);
    }
  };

  const getApprovalProgress = (offer) => {
    const approved = offer.approvals?.filter(a => a.status === 'approved').length || 0;
    const total = offer.approvals?.length || 0;
    return { approved, total };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Offers Management</h1>
          <p className="text-gray-500">Track and manage job offers and approvals</p>
        </div>
        {canManage && (
          <Button onClick={openCreateDialog} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Create Offer
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setFilters({...filters, status: 'pending_approval'})}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-yellow-600 text-sm mb-1"><Clock className="w-4 h-4" />Pending Approval</div>
                <p className="text-2xl font-bold">{offers.filter(o => o.approval_status === 'pending_approval').length}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setFilters({...filters, status: 'approved'})}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-green-600 text-sm mb-1"><CheckCircle2 className="w-4 h-4" />Approved</div>
                <p className="text-2xl font-bold">{offers.filter(o => o.approval_status === 'approved').length}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setFilters({...filters, candidate_decision: 'accepted'})}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-blue-600 text-sm mb-1"><ThumbsUp className="w-4 h-4" />Accepted</div>
                <p className="text-2xl font-bold">{offers.filter(o => o.candidate_decision === 'accepted').length}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all" onClick={() => setFilters({...filters, candidate_decision: 'declined'})}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-red-600 text-sm mb-1"><ThumbsDown className="w-4 h-4" />Declined</div>
                <p className="text-2xl font-bold">{offers.filter(o => o.candidate_decision === 'declined').length}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Approvals for Current User */}
      {pendingApprovals.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="w-5 h-5" />
              Pending Your Approval ({pendingApprovals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingApprovals.map((offer) => (
                <div key={offer.id} className="bg-white p-4 rounded-lg border flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold">{offer.candidate_name}</h4>
                    <p className="text-sm text-gray-500">{offer.requisition_title}</p>
                    <p className="text-sm font-medium text-green-600">₹{offer.offered_ctc} LPA</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => {
                        setSelectedOffer(offer);
                        setApprovalAction('reject');
                        setShowApprovalDialog(true);
                      }}
                    >
                      <ThumbsDown className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setSelectedOffer(offer);
                        setApprovalAction('approve');
                        setShowApprovalDialog(true);
                      }}
                    >
                      <ThumbsUp className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Approval Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.candidate_decision}
              onValueChange={(value) => setFilters(prev => ({ ...prev, candidate_decision: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Candidate Decision" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Decisions</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="negotiating">Negotiating</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Offers List */}
      <Card>
        <CardHeader>
          <CardTitle>All Offers ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : offers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No offers found.
            </div>
          ) : (
            <div className="space-y-4">
              {offers.map((offer) => {
                const progress = getApprovalProgress(offer);
                return (
                  <div key={offer.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-gray-400" />
                          <h3 className="font-semibold">{offer.candidate_name}</h3>
                          <Badge className={approvalStatusColors[offer.approval_status]}>
                            {offer.approval_status?.replace('_', ' ')}
                          </Badge>
                          <Badge className={candidateDecisionColors[offer.candidate_decision]}>
                            Candidate: {offer.candidate_decision}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {offer.requisition_title}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            ₹{offer.offered_ctc} LPA
                          </span>
                          {offer.joining_bonus > 0 && (
                            <span>+ ₹{offer.joining_bonus} bonus</span>
                          )}
                          {offer.proposed_joining_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(offer.proposed_joining_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        {/* Approval Progress */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">Approvals:</span>
                          <div className="flex gap-1">
                            {offer.approvals?.map((approval, idx) => (
                              <div
                                key={idx}
                                className={`w-8 h-2 rounded ${
                                  approval.status === 'approved' ? 'bg-green-500' :
                                  approval.status === 'rejected' ? 'bg-red-500' :
                                  approval.status === 'pending' ? 'bg-gray-300' : 'bg-gray-200'
                                }`}
                                title={`${approval.role}: ${approval.status}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-400">
                            ({progress.approved}/{progress.total})
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {canManage && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditOffer(offer)}
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => { setSelectedOffer(offer); setShowDeleteDialog(true); }}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {offer.approval_status === 'approved' && offer.candidate_decision === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedOffer(offer);
                              setShowDecisionDialog(true);
                            }}
                          >
                            Record Decision
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/recruitment/candidates/${offer.candidate_id}`)}
                        >
                          View Candidate
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Pagination */}
          {total > 20 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <span className="flex items-center px-4">Page {page}</span>
              <Button variant="outline" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalAction === 'approve' ? 'Approve' : 'Reject'} Offer
            </DialogTitle>
            <DialogDescription>
              {selectedOffer?.candidate_name} - ₹{selectedOffer?.offered_ctc} LPA
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Comments (optional)</label>
            <Textarea
              value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)}
              placeholder="Add any comments..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
            <Button
              onClick={handleApproval}
              disabled={submitting}
              className={approvalAction === 'approve' ? 'bg-green-600' : 'bg-red-600'}
            >
              {submitting ? 'Processing...' : approvalAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Candidate Decision Dialog */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Candidate Decision</DialogTitle>
            <DialogDescription>
              {selectedOffer?.candidate_name}&apos;s response to the offer
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium">Decision</label>
              <Select value={candidateDecision} onValueChange={setCandidateDecision}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                  <SelectItem value="negotiating">Negotiating</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {candidateDecision === 'accepted' && (
              <div>
                <label className="text-sm font-medium">Joining Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-3 py-2"
                  value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                />
              </div>
            )}
            {candidateDecision === 'declined' && (
              <div>
                <label className="text-sm font-medium">Reason for Declining</label>
                <Textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Why did the candidate decline?"
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>Cancel</Button>
            <Button onClick={handleCandidateDecision} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Offer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOffer ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
            <DialogDescription>
              {selectedOffer ? 'Update offer details' : 'Fill in the offer details for the selected candidate'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleCreateOffer(); }} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Candidate *</Label>
                <Select 
                  value={offerForm.candidate_id} 
                  onValueChange={(v) => setOfferForm({...offerForm, candidate_id: v})}
                  disabled={!!selectedOffer}
                >
                  <SelectTrigger><SelectValue placeholder="Select candidate" /></SelectTrigger>
                  <SelectContent>
                    {candidates.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.current_designation}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Position *</Label>
                <Input value={offerForm.position} onChange={(e) => setOfferForm({...offerForm, position: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={offerForm.department} onChange={(e) => setOfferForm({...offerForm, department: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Base Salary (₹) *</Label>
                <Input type="number" value={offerForm.base_salary} onChange={(e) => setOfferForm({...offerForm, base_salary: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Joining Bonus (₹)</Label>
                <Input type="number" value={offerForm.joining_bonus} onChange={(e) => setOfferForm({...offerForm, joining_bonus: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Variable Pay (₹)</Label>
                <Input type="number" value={offerForm.variable_pay} onChange={(e) => setOfferForm({...offerForm, variable_pay: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Stock Options</Label>
                <Input type="number" value={offerForm.stock_options} onChange={(e) => setOfferForm({...offerForm, stock_options: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Offer Expiry Date</Label>
                <Input type="date" value={offerForm.offer_expiry_date} onChange={(e) => setOfferForm({...offerForm, offer_expiry_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Proposed Joining Date</Label>
                <Input type="date" value={offerForm.proposed_joining_date} onChange={(e) => setOfferForm({...offerForm, proposed_joining_date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Other Benefits</Label>
              <Textarea value={offerForm.other_benefits} onChange={(e) => setOfferForm({...offerForm, other_benefits: e.target.value})} placeholder="Health insurance, gym membership, etc." rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={offerForm.notes} onChange={(e) => setOfferForm({...offerForm, notes: e.target.value})} placeholder="Internal notes about this offer" rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting || !offerForm.candidate_id || !offerForm.position || !offerForm.base_salary}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedOffer ? 'Update Offer' : 'Create Offer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Offer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the offer for {selectedOffer?.candidate_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteOffer} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
